import { createElement } from '../../../core/dom/helpers.js';
import { normalizeFieldsets } from '../../../core/form-builder/form-structure.js';
import { createFieldsetCard } from './fieldset-card.js';

/**
 * @typedef {import('../../../contracts/forms.js').FormBuilderFieldset} FormBuilderFieldset
 * @typedef {import('../../../contracts/forms.js').FormBuilderField} FormBuilderField
 */

/** Monotonic fieldset-key source (top level). Keys are never reused. */
let fsKeySeq = 0;
function nextFsKey() { return `fs-${++fsKeySeq}`; }

/** A fresh empty fieldset. @returns {FormBuilderFieldset} */
function blankFieldset() { return { name: '', fields: [] }; }

/** Deep keyless clone of a fieldset list (the emit snapshot). @param {FormBuilderFieldset[]} fieldsets */
function cloneFieldsets(fieldsets) {
  return fieldsets.map((fs) => ({
    name: fs.name,
    fields: fs.fields.map((f) => ({
      cssClass: f.cssClass,
      label: f.label,
      title: f.title,
      required: f.required,
      multiple: f.multiple,
      options: f.options.map((o) => ({ value: o.value, baseline: o.baseline })),
    })),
  }));
}

/**
 * Form-builder driver — a list of fieldset cards, each holding an ordered list of field rows.
 * The top of a three-layer composite (driver → fieldset-card → field-row), structured exactly
 * like the videos driver (driver → video-card) one level deeper.
 *
 * State ownership:
 * - The driver owns the fieldset list (`currentValues`) and the fieldset keys (`fsKeys`),
 *   lockstep but separate — the videos `currentValues`/`keys` discipline.
 * - Each fieldset-card owns its field keys; each field-row owns its option keys. Keys are
 *   driver-private and NEVER part of the emitted value (the codec serializes a keyless model).
 *
 * Emit discipline (matches videos):
 * - `emit()` sends a deep keyless clone, only on real user change.
 * - `setValue`/`clearValue` rebuild silently (no emit).
 * - Patches (name/field edits) update state + emit but do NOT re-render (focus-preserving);
 *   add/remove/move re-render only the affected level.
 *
 * @type {import('../../../contracts/forms.js').FieldDriver}
 */
export const formBuilderDriver = {
  type: 'form-builder',
  /**
   * @param {HTMLElement} container
   * @param {import('../../../contracts/forms.js').FieldDriverContext} ctx
   * @returns {import('../../../contracts/forms.js').FieldDriverHandle}
   */
  mount(container, ctx) {
    const { onChange } = ctx;
    const def = /** @type {import('../../../contracts/forms.js').FormBuilderFieldDef} */ (ctx.def);

    /** @type {FormBuilderFieldset[]} */
    let currentValues = normalizeFieldsets(ctx.value);
    /** @type {string[]} Stable fieldset keys, lockstep with currentValues. */
    let fsKeys = currentValues.map(() => nextFsKey());
    /** @type {ReturnType<typeof createFieldsetCard>[]} Live fieldset-card handles. */
    let cardHandles = [];
    let disabled = Boolean(def.disabled);

    const wrapper = createElement('div', { class: 'form-builder' });
    const list = createElement('div', { class: 'form-builder__fieldsets' });
    const addBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
      type: 'button', class: 'form-builder__add btn btn-primary',
    }, ['Add fieldset']));
    addBtn.disabled = disabled;

    wrapper.appendChild(list);
    wrapper.appendChild(addBtn);
    container.appendChild(wrapper);

    /** Emit a fresh deep keyless snapshot (only called on real user change). */
    function emit() { onChange(cloneFieldsets(currentValues)); }

    /** @type {string | null} */
    let draggedKey = null;

    // --- fieldset-level mutations ---------------------------------------------

    /** Patch the fieldset at the index resolved from `key`. @param {string} key @param {Partial<FormBuilderFieldset>} patch */
    function patchFieldset(key, patch) {
      const i = fsKeys.indexOf(key);
      if (i === -1) return;
      currentValues = currentValues.map((fs, j) => (j === i ? { ...fs, ...patch } : fs));
      emit();
    }

    function addFieldset() {
      currentValues = [...currentValues, blankFieldset()];
      fsKeys = [...fsKeys, nextFsKey()];
      renderAll();
      emit();
    }

    /** @param {number} i */
    function removeAt(i) {
      if (i < 0 || i >= currentValues.length) return;
      currentValues = [...currentValues.slice(0, i), ...currentValues.slice(i + 1)];
      fsKeys = [...fsKeys.slice(0, i), ...fsKeys.slice(i + 1)];
      renderAll();
      emit();
    }

    /** @param {number} from @param {number} to */
    /**
     * @param {number} from @param {number} to @param {{ focus?: boolean }} [opts]
     * `opts.focus` (set ONLY by the fieldset Move buttons, never by drag) re-homes keyboard focus
     * onto the moved fieldset's Move button after the rebuild — a drag must NOT steal focus.
     */
    function moveItem(from, to, opts = {}) {
      if (from === to || from < 0 || to < 0 || from >= currentValues.length || to >= currentValues.length) return;
      const movedKey = fsKeys[from];
      const movingUp = to < from;
      const nv = [...currentValues]; const nk = [...fsKeys];
      const [mv] = nv.splice(from, 1); const [mk] = nk.splice(from, 1);
      nv.splice(to, 0, mv); nk.splice(to, 0, mk);
      currentValues = nv; fsKeys = nk;
      renderAll();
      emit();
      if (opts.focus) cardHandles[fsKeys.indexOf(movedKey)]?.focusMove?.(movingUp);
    }

    /** Build the fieldset-card at `idx` with drag SPLIT (handle = origin, root = drop target). @param {number} idx */
    function makeCard(idx) {
      const card = createFieldsetCard({
        key: fsKeys[idx],
        value: currentValues[idx],
        onNameChange: (key, name) => patchFieldset(key, { name }),
        onFieldsChange: (key, fields) => patchFieldset(key, { fields }),
        onRemove: (key) => { if (!disabled) removeAt(fsKeys.indexOf(key)); },
        onMoveUp: (key) => { if (disabled) return; const i = fsKeys.indexOf(key); moveItem(i, i - 1, { focus: true }); },
        onMoveDown: (key) => { if (disabled) return; const i = fsKeys.indexOf(key); moveItem(i, i + 1, { focus: true }); },
      });
      cardHandles.push(card);
      const root = card.el;
      root.dataset.fsIdx = String(idx);

      const cardHandle = card.handle;
      cardHandle.addEventListener('dragstart', (e) => {
        if (disabled) { e.preventDefault(); return; }
        draggedKey = fsKeys[idx];
        root.classList.add('fb-fieldset--dragging');
        if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
      });
      cardHandle.addEventListener('dragend', () => {
        draggedKey = null;
        root.classList.remove('fb-fieldset--dragging');
        list.querySelectorAll('.fb-fieldset--over').forEach((el) => el.classList.remove('fb-fieldset--over'));
      });
      root.addEventListener('dragover', (e) => {
        if (draggedKey === null) return;
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        root.classList.add('fb-fieldset--over');
      });
      root.addEventListener('dragleave', () => root.classList.remove('fb-fieldset--over'));
      root.addEventListener('drop', (e) => {
        e.preventDefault();
        root.classList.remove('fb-fieldset--over');
        if (draggedKey === null) return;
        moveItem(fsKeys.indexOf(draggedKey), parseInt(root.dataset.fsIdx ?? '-1', 10));
      });
      return root;
    }

    function renderAll() {
      if (cardHandles.length) { for (const h of cardHandles) h.destroy(); cardHandles = []; }
      list.textContent = '';
      currentValues.forEach((_fs, i) => list.appendChild(makeCard(i)));
      const last = cardHandles.length - 1;
      cardHandles.forEach((h, i) => {
        h.setMoveState(i > 0, i < last);
        h.setDisabled(disabled);
      });
    }

    addBtn.addEventListener('click', () => { if (!disabled) addFieldset(); });

    renderAll();

    return {
      getValue() {
        return cloneFieldsets(currentValues);
      },
      setValue(value) {
        currentValues = normalizeFieldsets(value);
        fsKeys = currentValues.map(() => nextFsKey());
        renderAll();
      },
      clearValue() {
        if (currentValues.length === 0) return; // idempotent; never emits
        currentValues = [];
        fsKeys = [];
        renderAll();
      },
      setDisabled(d) {
        disabled = d;
        addBtn.disabled = d;
        for (const h of cardHandles) h.setDisabled(d);
      },
      setVisible(visible) {
        wrapper.hidden = !visible;
      },
      setErrors() {},
      setAriaInvalid(invalid) {
        wrapper.setAttribute('aria-invalid', invalid ? 'true' : 'false');
        // Surface the specific half-specified field hints when the host flags the field.
        if (invalid) for (const h of cardHandles) h.refreshValidity();
      },
      destroy() {
        for (const h of cardHandles) h.destroy();
        cardHandles = [];
        wrapper.remove();
      },
    };
  },
};
